<template>
  <v-data-table
    id="employees-table"
    :headers="headers"
    :items="filteredEmployees"
    :sort-by.sync="sortBy"
    :sort-desc.sync="sortDesc"
    virtual-rows
    fixed-header
    :search="search"
    :custom-filter="customfilter"
    :loading="loading"
    :expanded.sync="expanded"
    @click:row="
      (item) => {
        if (!reason) expand(item);
      }
    "
    :no-data-text="noDataText()"
    footer.page-text="page text"
    :footer-props="{
      'items-per-page-text': `${$t('employees.footer.itemsPerPage')}`,
      'items-per-page-all-text': `${$t('employees.footer.all')}`,
    }"
    class="safety-elevation-3 table-striped"
  >
    <template v-slot:top>
      <v-row align="center" class="mx-5">
        <v-text-field
          class="flex-grow-1 pr-4 py-5"
          v-model="search"
          prepend-inner-icon="search"
          :label="$t('search.title')"
          single-line
          :background-color="
            $vuetify.theme.themes.light.primary | hexToRgb(0.2)
          "
          solo
          dense
          flat
          hide-details
          data-test="employees-search"
        />
        <v-btn
          text
          color="primary"
          @click="
            () => {
              filter = !filter;
              filters = [];
            }
          "
          >{{ $t("instructions.filter.title") }}
          <v-icon right>{{ filter ? "expand_less" : "expand_more" }}</v-icon>
        </v-btn>
        <v-menu bottom left>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <template v-for="tc in hidableHeaders">
              <v-list-item :key="tc" @click="setColumnVisibility(tc)">
                {{
                  `${
                    headers.some((h) => h.value == tc)
                      ? $t("dialog.hide")
                      : $t("dialog.show")
                  } ${
                    tc == "manager"
                      ? $t("profile.manager").toLowerCase()
                      : $t("tags." + tc).toLowerCase()
                  }`
                }}
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </v-row>
      <v-row v-if="filter" align="center" class="mx-5">
        <template>
          <v-autocomplete
            class="flex-grow-1 mb-5"
            v-model="filters"
            :placeholder="
              `${$t('instructions.filter.title')} - ${$t(
                'instructions.filter.noTags'
              )}`
            "
            :items="tags"
            required
            chips
            deletable-chips
            solo
            hide-details
            :item-text="getDetailedTagText"
            multiple
            return-object
          >
            <template v-slot:item="{ item, attrs }">
              <v-list-item-action>
                <v-checkbox :input-value="attrs.inputValue"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{
                  getDetailedTagText(item)
                }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.explanation">
                  <span> {{ item.explanation }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </template>
      </v-row>
      <!-- alerts -->
      <v-row
        class="mx-5"
        v-if="
          role(
            'admin',
            'departmentManager',
            'manager',
            'safetyRepresentative'
          ) && showCounters
        "
      >
        <v-col class="pa-0 pb-3">
          <v-chip
            v-if="totalPendingSignatures > 0"
            @click="sort('pendingCount')"
            class="
              orange
              lighten-4
              warning--text
              text--darken-2 text-none
              font-weight-regular
              mr-6
            "
          >
            <v-icon class="mr-2" color="orange lighten-1"
              >notification_important</v-icon
            >
            {{
              $tc("employees.alert.pendingSignatures", totalPendingSignatures)
            }}
          </v-chip>

          <v-chip
            v-if="totalOverdueSignatures > 0"
            @click="sort('pendingCount')"
            class="
              deep-orange
              lighten-4
              error--text
              text--darken-3 text-none
              font-weight-regular
              mr-6
            "
            depressed
          >
            <v-icon class="mr-2" color="error lighten-1">warning</v-icon>
            {{
              $tc("employees.alert.overdueSignatures", totalOverdueSignatures)
            }}
          </v-chip>

          <v-chip
            v-if="totalPendingConfirmations > 0"
            @click="sort('unconfirmedCount')"
            class="
              orange
              lighten-4
              warning--text
              text--darken-2 text-none
              font-weight-regular
              mr-6
            "
            depressed
          >
            <v-icon class="mr-2" color="orange lighten-1"
              >notification_important</v-icon
            >
            {{
              $tc(
                "employees.alert.pendingConfirmations",
                totalPendingConfirmations
              )
            }}
          </v-chip>

          <v-chip
            v-if="
              appSettings.isRiskAnalysisSigningEnabled &&
                totalPendingRiskSignatures > 0
            "
            @click="sort('pendingRiskSignCount')"
            class="
              orange
              lighten-4
              warning--text
              text--darken-2 text-none
              font-weight-regular
              mr-6
            "
            depressed
          >
            <v-icon class="mr-2" color="orange lighten-1"
              >notification_important</v-icon
            >
            {{
              $tc(
                "employees.alert.pendingRiskSignatures",
                totalPendingRiskSignatures
              )
            }}
          </v-chip>

          <template v-for="[mode, employees] in modeEmployeesMap">
            <v-chip
              :key="mode"
              @click="search = mode"
              class="
                orange
                lighten-4
                warning--text
                text--darken-2 text-none
                font-weight-regular
                mr-6
              "
              depressed
            >
              <v-icon class="mr-2" color="orange lighten-1"
                >notification_important</v-icon
              >
              {{ $tc("employees.alert.activeModes", employees.length) }}
              {{ mode }}
            </v-chip>
          </template>
        </v-col>
      </v-row>
    </template>

    <template v-slot:[`footer.page-text`]="items">
      {{ items.pageStart }} - {{ items.pageStop }} /
      {{ items.itemsLength }}
    </template>

    <template v-slot:[`item.id`]="{ item: e }">
      <div class="align-center-on-print">
        <span :style="e.isActive ? '' : 'text-decoration: line-through;'">
          {{ e.id | id }}
        </span>
        <br />
        <span v-if="e.employeeId" class="text--disabled">
          ({{ e.employeeId }})
        </span>
      </div>
    </template>

    <template v-slot:[`item.role`]="{ item: e }">{{
      $t("role." + e.role)
    }}</template>

    <template v-slot:[`item.lastName`]="{ item: e }">
      <router-link :to="{ name: 'employee', params: { id: e.id } }">{{
        e | name
      }}</router-link>
      <v-tooltip
        right
        v-if="
          role(
            'admin',
            'safetyRepresentative',
            'departmentManager',
            'manager'
          ) &&
            e.missingParentTags &&
            e.missingParentTags.length
        "
      >
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on">{{ ` *` }}</span>
        </template>
        <span
          >{{ $t("tags.missingParentTags") }}
          {{ e.missingParentTags.map((t) => t.name).join(", ") }}</span
        >
      </v-tooltip>
    </template>

    <template v-slot:[`item.manager`]="{ item: e }">
      <router-link
        v-if="e.manager"
        :to="{ name: 'employee', params: { id: e.manager.id } }"
        >{{ e.manager | name }}</router-link
      >
    </template>

    <template v-slot:[`item.department`]="{ item: e }">
      <employee-tags :tags="e.department" />
    </template>

    <template v-slot:[`item.location`]="{ item: e }">
      <employee-tags :tags="e.location" />
    </template>

    <template v-slot:[`item.position`]="{ item: e }">
      <employee-tags :tags="e.position" />
    </template>

    <template v-slot:[`item.shift`]="{ item: e }">
      <employee-tags :tags="e.shift" />
    </template>

    <template v-slot:[`item.collar`]="{ item: e }">
      <employee-tags :tags="e.collar" />
    </template>

    <template v-slot:[`item.started`]="{ item: e }">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <span
            v-if="e.started !== '1970-01-01T00:00:00Z'"
            v-bind="attrs"
            v-on="on"
            >{{ e.started | date }}</span
          >
          <span v-else v-bind="attrs" v-on="on">{{
            $t("employees.manuallyImported")
          }}</span></template
        >

        <span>{{ $t("employees.column.added") }} {{ e.created | date }}</span>
      </v-tooltip>
    </template>

    <template v-slot:[`item.pendingCount`]="{ item: e }">
      <v-chip
        v-if="e.overdueCount"
        small
        class="deep-orange lighten-4 error--text text--darken-3 text-uppercase"
      >
        <v-avatar left class="mr-0" data-test="employees-overdueCount">{{
          e.overdueCount
        }}</v-avatar>
        {{ $t("employees.alert.overdueSignaturesShort") }}
      </v-chip>
      <v-chip
        v-show="e.pendingCount && !e.overdueCount"
        small
        class="orange lighten-4 warning--text text--darken-2 text-uppercase"
      >
        <v-avatar
          left
          class="mr-0 text-uppercase"
          data-test="employees-pendingCount"
          >{{ e.pendingCount }}</v-avatar
        >
        {{ $t("employees.alert.pendingSignaturesShort") }}
      </v-chip>
      <v-chip
        v-show="!e.pendingCount && !e.overdueCount"
        small
        class="green lighten-4 success--text text--darken-2"
        >{{ $t("employees.alert.ok") }}</v-chip
      >
    </template>

    <template v-slot:[`item.unconfirmedCount`]="{ item: e }">
      <employee-instructions-dialog
        v-if="e.unconfirmedCount"
        :employee="e"
        @refresh="$emit('refresh')"
      />
      <v-chip
        v-else
        small
        class="green lighten-4 success--text text--darken-2"
        >{{ $t("employees.alert.ok") }}</v-chip
      >
    </template>

    <template v-slot:[`item.pendingRiskSignCount`]="{ item: e }">
      <v-chip
        v-if="e.pendingRiskSignCount"
        small
        class="orange lighten-4 warning--text text--darken-2 text-uppercase"
      >
        {{ e.pendingRiskSignCount }}&nbsp;
        {{ $t("employees.alert.pendingSignaturesShort") }}
      </v-chip>
      <v-chip v-else small class="green lighten-4 success--text text--darken-2">
        {{ $t("employees.alert.ok") }}
      </v-chip>
    </template>

    <template v-slot:[`item.action`]="{ item: e }">
      <v-btn
        :color="
          e.noSignatureFound ? 'error' : e.rereadingForced ? 'black' : 'primary'
        "
        small
        @click="forceRereading(e)"
        :text="e.rereadingForced"
        :style="{ pointerEvents: e.rereadingForced ? 'none' : 'auto' }"
        :loading="e.loading"
      >
        {{
          e.noSignatureFound
            ? $t("instructions.forceRereading.noSignatureFound")
            : e.rereadingForced
            ? $t("instructions.forceRereading.sent")
            : $t("instructions.forceRereading.button")
        }}
      </v-btn>
    </template>

    <template v-slot:header.action>
      <v-btn color="primary" small @click="forceRereadingAll">
        {{ $t("instructions.forceRereading.forceAll") }}
      </v-btn>
    </template>

    <template v-slot:no-results>{{
      $t("search.noResults", { item: search })
    }}</template>

    <template v-slot:expanded-item="{ headers, item }">
      <td v-if="expanded.length" :colspan="headers.length">
        <assignment-button-ribbon
          :employee="item"
          @refresh="$emit('refresh')"
        />
      </td>
    </template>
  </v-data-table>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import TagsQuery from "@/graphql/Tags.gql";
import { getDetailedTagText } from "@/utils/tag";

import AssignmentButtonRibbon from "@/components/AssignmentButtonRibbon.vue";
import EmployeeTags from "@/components/employee/EmployeeTags.vue";
import EmployeeInstructionsDialog from "@/components/employee/EmployeeInstructionsDialog.vue";
import ForceRereadingMutation from "@/graphql/ForceRereadingEmployee.gql";
import ForceRereadingAllMutation from "@/graphql/ForceRereadingEmployees.gql";

export default {
  components: {
    AssignmentButtonRibbon,
    EmployeeTags,
    EmployeeInstructionsDialog,
  },
  props: {
    tagCategories: {
      type: Array,
      required: true,
      default: () => [],
    },
    employees: {
      type: Array,
      required: true,
      default: () => [],
    },
    loading: {
      type: Boolean,
      required: true,
    },
    showCounters: {
      type: Boolean,
      required: false,
      default: true,
    },
    reason: {
      type: String,
      required: false,
    },
    versionId: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      search: "",
      filter: false,
      filters: [],
      sortBy: "",
      sortDesc: true,
      expanded: [],
      hidableHeaders: ["collar", "location", "manager"],
      optionalHeaders: [],
    };
  },

  watch: {
    filters() {
      this.$store.dispatch("change_employee_filters", this.filters);
    },
    optionalHeaders() {
      this.$store.dispatch("change_employee_headers", this.optionalHeaders);
      this.$emit("refresh");
    },
  },

  mounted() {
    this.filters = this.employeeFilters || [];
    if (this.filters.length) {
      this.filter = true;
    }
    this.showAll = this.showAllEmployees;
    this.optionalHeaders = this.employeeHeaders || [];
  },

  computed: {
    ...mapState(["appSettings"]),
    ...mapGetters([
      "role",
      "employeeFilters",
      "showAllEmployees",
      "employeeHeaders",
    ]),
    headers() {
      let h = [
        {
          text: this.$i18n.t("employees.column.id"),
          value: "id",
          class: "text-uppercase subheading",
          align: "right",
        },
        {
          text: this.$i18n.t("employees.column.name"),
          value: "lastName",
          class: "text-uppercase subheading",
        },
        {
          text: this.$i18n.t("employees.column.role"),
          value: "role",
          class: "text-uppercase subheading",
        },
        {
          text: this.$i18n.t("employees.column.started"),
          value: "started",
          class: "text-uppercase subheading text-truncate",
        },
      ];

      if (this.showCounters) {
        h.push(
          {
            text: this.$i18n.t("employees.column.signatures"),
            value: "pendingCount",
            class: "text-uppercase subheading",
            align: "left",
          },
          {
            text: this.$i18n.t("employees.column.confirmations"),
            value: "unconfirmedCount",
            class: "text-uppercase subheading",
            align: "left",
          }
        );

        if (
          this.showCounters &&
          this.appSettings.isRiskAnalysisSigningEnabled
        ) {
          h.push({
            text: this.$i18n.t("employees.column.riskAnalyses"),
            value: "pendingRiskSignCount",
            class: "text-uppercase subheading",
            align: "left",
          });
        }
      }

      Array.isArray(this.tagCategories) &&
        Array.isArray(this.optionalHeaders) &&
        this.tagCategories
          .filter((x) => !this.hidableHeaders.includes(x))
          .concat(this.optionalHeaders.filter((x) => x != "manager"))
          .forEach((value, i) => {
            h.splice(3 + i, 0, {
              text: this.$i18n.t(`tags.${value}`),
              value: value,
              class: "text-uppercase subheading text-truncate",
              sort: this.sortTags,
            });
          });
      if (this.optionalHeaders.includes("manager")) {
        h.push({
          text: this.$i18n.t("profile.manager"),
          value: "manager",
          class: "text-uppercase subheading",
          sort: this.sortManagers,
        });
      }
      if (this.reason) {
        h.push({
          text: "",
          value: "action",
          sortable: false,
        });
      }

      return h;
    },
    filteredEmployees() {
      if (!this.employees) {
        return [];
      } else if (this.filters.length) {
        return this.employees.filter((e) =>
          e.tags.some((t) => this.filters.some((f) => f.id == t.id))
        );
      } else if (this.filter && !this.filters.length) {
        return this.employees.filter((e) => !e.tags.length);
      } else {
        return this.employees;
      }
    },
    totalPendingSignatures() {
      return this.filteredEmployees.reduce(
        (count, e) => count + e.pendingCount,
        0
      );
    },
    totalOverdueSignatures() {
      return this.filteredEmployees.reduce(
        (count, e) => count + e.overdueCount,
        0
      );
    },
    totalPendingConfirmations() {
      return this.filteredEmployees.reduce(
        (count, e) => count + e.unconfirmedCount,
        0
      );
    },
    totalPendingRiskSignatures() {
      return this.filteredEmployees.reduce(
        (count, e) => count + e.pendingRiskSignCount,
        0
      );
    },
    modeEmployeesMap() {
      const map = new Map();
      this.filteredEmployees &&
        this.filteredEmployees.forEach((e) => {
          e.modes.forEach((mode) => {
            const name = mode.name;
            if (map.has(name)) {
              const val = map.get(name);
              val.push(`${e.id}`);
            } else {
              map.set(name, [`${e.id}`]);
            }
          });
        });

      return map;
    },
  },

  methods: {
    getDetailedTagText: getDetailedTagText,

    setColumnVisibility(header) {
      this.optionalHeaders.some((x) => x == header)
        ? (this.optionalHeaders = this.optionalHeaders.filter(
            (x) => x != header
          ))
        : this.optionalHeaders.push(header);
    },
    expand(employee) {
      if (
        !this.role(
          "admin",
          "safetyRepresentative",
          "departmentManager",
          "manager"
        )
      ) {
        return;
      }
      if (this.expanded.length > 0) {
        this.expanded = [];
      } else {
        this.expanded.push(employee);
      }
    },

    sort(by, desc = true) {
      this.sortBy = by;
      this.sortDesc = desc;
    },

    customfilter(value, search, item) {
      return (
        !search ||
        search
          .toLowerCase()
          .split(" ")
          .every((w) => item.search.includes(w))
      );
    },

    noDataText() {
      return this.loading ? "Loading..." : this.$i18n.t("employees.noData");
    },
    sortTags(a, b) {
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length == 1 && b.length == 1) {
          if (a[0].description && b[0].description) {
            return a[0].description.localeCompare(b[0].description);
          }
          return a[0].name.localeCompare(b[0].name);
        }
        return a.length - b.length;
      } else if (typeof a === "string" && typeof b === "string") {
        return a.localeCompare(length);
      } else {
        return 0;
      }
    },
    sortManagers(a, b) {
      const aName = a ? `${a.lastName} ${a.firstName}` : "";
      const bName = b ? `${b.lastName} ${b.firstName}` : "";
      return aName.localeCompare(bName);
    },
    forceRereading(employee) {
      this.$set(employee, "loading", true);
      this.$apollo
        .mutate({
          mutation: ForceRereadingMutation,
          variables: {
            employeeId: employee.id,
            versionId: this.versionId,
            reason: this.reason,
          },
        })
        .then((res) => {
          this.$set(employee, "loading", false);
          this.$set(employee, "rereadingForced", true);
          if (!res.data.forceRereadingEmployee) {
            this.$set(employee, "noSignatureFound", true);
          }
        })
        .catch((err) => {
          this.$set(employee, "loading", false);
          this.$store.commit("push_global_message", {
            text: `${this.$i18n.t("error.saveFailed")} ${err}`,
            color: "error",
          });
        });
    },
    forceRereadingAll() {
      const unforcedEmployees = this.filteredEmployees.filter((employee) => {
        return (
          this.customfilter(null, this.search, employee) &&
          !employee.rereadingForced
        );
      });

      if (unforcedEmployees.length === 0) {
        this.$store.commit("push_global_message", {
          text: this.$i18n.t("instructions.forceRereading.allForced"),
          color: "warning",
        });
        return;
      }

      unforcedEmployees.forEach((employee) => {
        this.$set(employee, "loading", true);
      });

      const employeeIds = unforcedEmployees.map((employee) => employee.id);
      const variables = {
        employeeIds,
        versionId: this.versionId,
        reason: this.reason,
      };

      this.$apollo
        .mutate({
          mutation: ForceRereadingAllMutation,
          variables,
        })
        .then((response) => {
          const revokedSignatures = response.data.forceRereadingEmployees;
          if (revokedSignatures) {
            unforcedEmployees.forEach((employee) => {
              const revokedSignature = revokedSignatures.find(
                (signature) => signature.employee.id === employee.id
              );
              this.$set(employee, "loading", false);
              this.$set(employee, "rereadingForced", true);
              if (!revokedSignature) {
                this.$set(employee, "noSignatureFound", true);
              }
            });
          } else {
            unforcedEmployees.forEach((employee) => {
              this.$set(employee, "loading", false);
              this.$set(employee, "rereadingForced", true);
              this.$set(employee, "noSignatureFound", true);
            });
          }
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            text: `${this.$i18n.t("error.saveFailed")} ${err}`,
            color: "error",
          });
          unforcedEmployees.forEach((employee) => {
            this.$set(employee, "loading", false);
          });
        });
    },
  },

  apollo: {
    tags: {
      query: TagsQuery,
    },
  },
};
</script>

<style>
@media print {
  #employees-table th,
  #employees-table td {
    font-size: 0.7em;
    padding: 0;
  }
  #employees-table .v-chip {
    font-size: 0.7em;
    height: 16px;
    padding: 0.4em !important;
  }
  .align-center-on-print {
    text-align: center;
  }
}
</style>
